import React from 'react'
import { useAtomValue } from 'jotai'

import { Access } from '../../lib'
import * as state from '../../states'
import { TOfferSpendByAccountAggregated } from '../../types'
import { IUiTableConfig, UiTable, UiDateInfo } from '../ui'
import * as helpers from '../../helpers'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IOfferSpendByAccountDetailsTableProps = {
	items: Array<TOfferSpendByAccountAggregated>;
	skip: number;
	showDateColumn: boolean;
}

export function sortOfferSpendItemsByDate<T extends { at: Date }>(items: Array<T>) {
	return [...items].sort((a, b) => b.at.getTime() - a.at.getTime())
}

export const OfferSpendByAccountDetailsTable = (props: IOfferSpendByAccountDetailsTableProps) => {
	const {
		items = [],
		skip = 0,
		showDateColumn = false
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const tableConfig: IUiTableConfig<TOfferSpendByAccountAggregated>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_id)
		},
		{
			key: 'at',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_date),
			rowCellFormatter: ({ value, rowData }) => {
				return (
					<UiDateInfo value={value}/>
				)
			}
		},
		{
			key: 'amount',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_amount),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const formatValue = helpers.formatAmount(rowData.amount)

				return <>{helpers.thousandSeparator(formatValue)}</>
			}
		},
		{
			key: 'offerId',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_offer),
			rowCellFormatter: ({ rowData }): React.JSX.Element => {
				const offerId = rowData?.offerId ?? null
				const offer = rowData?.offer ?? null
				const name = offer?.name ?? null
				return <>
					<strong>[id: {offerId}]</strong>
					&nbsp;
					{name ?? '?'}
				</>
			}
		},
		{
			key: 'scopeId',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_scope),
			rowCellFormatter: ({ rowData }) => {
				const offer = rowData?.offer ?? null
				const scope = offer?.scope ?? null
				const scopeId = offer?.scopeId ?? null
				const name = scope?.name ?? null
				return <>
					<strong>[id: {scopeId}]</strong>
					&nbsp;
					{name ?? '?'}
				</>
			}
		},
		{
			key: 'notes',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_notes),
			rowCellFormatter: ({ value, rowData }) => {
				return <>{value ?? ''}</>
			}
		}
	]

	const tableConfigFiltered = tableConfig.filter(item => {
		if (!showDateColumn && item.key === 'at') return false
		return true
	})

	const sortedItemsByDate = sortOfferSpendItemsByDate(items)

	return <>
		<UiTable
			data={sortedItemsByDate}
			config={tableConfigFiltered}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper table-sticky-wrapper--top-60',
			// }}
		/>
	</>
}